import React from 'react';
import { ExternalLink } from 'lucide-react';
import { ToolCardJob } from '../../../types/chat';
import { useCarouselJobCards } from '../../../hooks/useCarouselJobCards';

export interface CarouselProgressInfo {
  allDone: boolean;
  failedCount: number;
  total: number;
}

interface CarouselResultStripProps {
  jobs: ToolCardJob[];
  onProgress?: (info: CarouselProgressInfo) => void;
}

const FAILED = new Set(['failed', 'cancelled']);

const shortRole = (role?: string | null): string => (role ? role.split(/[:：]/)[0] : '');

/**
 * カルーセルのスライドを横並びで表示。各スライドは個別に進捗→完成。
 */
const CarouselResultStrip: React.FC<CarouselResultStripProps> = ({ jobs, onProgress }) => {
  const { slides, allDone } = useCarouselJobCards(jobs, jobs.length > 0);
  const failedCount = slides.filter((s) => FAILED.has(s.status)).length;
  const total = slides.length;
  React.useEffect(() => {
    onProgress?.({ allDone, failedCount, total });
  }, [allDone, failedCount, total, onProgress]);

  return (
    <div>
      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
        {slides.map((slide) => (
          <div key={slide.job_id} className="w-32 shrink-0">
            <div className="relative aspect-square overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
              {slide.image ? (
                <a href={slide.image.url} target="_blank" rel="noopener noreferrer">
                  <img src={slide.image.url} alt={`スライド${slide.slide_index + 1}`} className="h-full w-full object-cover" loading="lazy" />
                </a>
              ) : FAILED.has(slide.status) ? (
                <div className="flex h-full items-center justify-center px-2 text-center text-[11px] text-rose-600">失敗</div>
              ) : (
                <div className="flex h-full flex-col items-center justify-center gap-1 text-xs text-gray-500">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-teal-500 border-t-transparent" aria-hidden="true" />
                  <span>{slide.progress}%</span>
                </div>
              )}
              <span className="absolute left-1 top-1 rounded bg-white/90 px-1.5 text-[11px] font-medium text-gray-700">{slide.slide_index + 1}</span>
            </div>
            <p className="mt-1 truncate text-[11px] text-gray-500" title={slide.role ?? ''}>
              {shortRole(slide.role)}
            </p>
          </div>
        ))}
      </div>
      <a href="/ads/carousel" className="mt-1 inline-flex items-center gap-1 text-xs text-teal-700 hover:underline">
        <ExternalLink className="h-3 w-3" aria-hidden="true" />
        カルーセルページで開く
      </a>
    </div>
  );
};

export default CarouselResultStrip;
